import React from 'react';

/**
 * ErrorBoundary - Filet de sécurité pour éviter les écrans blancs en production.
 * Affiche un message de fallback + détails techniques (collapsibles) en cas
 * de crash de rendu dans la sous-arborescence enfant.
 *
 * Usage: <ErrorBoundary featureName="Planning"><Page /></ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.featureName || 'unknown', error, info);
    this.setState({ info });
    // Best-effort backend log so we can diagnose prod issues
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/client-error-log';
      const payload = {
        feature: this.props.featureName || 'unknown',
        message: String(error?.message || error || ''),
        stack: String(error?.stack || '').slice(0, 4000),
        component_stack: String(info?.componentStack || '').slice(0, 4000),
        url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        ts: new Date().toISOString(),
      };
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      } else if (typeof fetch !== 'undefined') {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_e) {
      // ignore — best effort
    }
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_e) {
      // ignore
    }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null, showDetails: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, info, showDetails } = this.state;
    const featureName = this.props.featureName || 'cette page';

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-xl w-full bg-white border border-rose-200 rounded-2xl shadow-sm p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-rose-700">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-slate-900">Une erreur est survenue</h2>
              <p className="text-sm text-slate-600 mt-1">
                Un problème inattendu nous a empêchés d'afficher <strong>{featureName}</strong>. Rechargez la page pour reprendre, ou réessayez. Si le problème persiste, contactez le support en partageant les détails techniques ci-dessous.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  type="button"
                  onClick={this.handleReload}
                  className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
                  data-testid="error-boundary-reload-btn"
                >
                  Recharger la page
                </button>
                <button
                  type="button"
                  onClick={this.handleReset}
                  className="px-3 py-1.5 rounded-md bg-white border text-slate-700 text-sm hover:bg-slate-50"
                  data-testid="error-boundary-retry-btn"
                >
                  Réessayer
                </button>
                <button
                  type="button"
                  onClick={() => this.setState({ showDetails: !showDetails })}
                  className="px-3 py-1.5 rounded-md text-slate-500 text-sm hover:text-slate-800"
                  data-testid="error-boundary-toggle-details"
                >
                  {showDetails ? 'Masquer les détails' : 'Voir les détails techniques'}
                </button>
              </div>

              {showDetails && (
                <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 max-h-64 overflow-auto" data-testid="error-boundary-details">
                  <p className="font-mono whitespace-pre-wrap break-words">
                    {String(error?.message || error || 'Erreur inconnue')}
                  </p>
                  {error?.stack && (
                    <pre className="mt-2 font-mono whitespace-pre-wrap break-words text-[10px] text-slate-500">{String(error.stack).slice(0, 2000)}</pre>
                  )}
                  {info?.componentStack && (
                    <pre className="mt-2 font-mono whitespace-pre-wrap break-words text-[10px] text-slate-500">{String(info.componentStack).slice(0, 2000)}</pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
